/**
 * Monte Carlo Service — wraps /api/admin/ai/analytics/simulations endpoints
 * Phase 4.10 β6 — WQ-241
 *
 * Backend router prefix: /api/admin/ai/analytics (Phase 4.8-D / WQ-194).
 * Wraps create + run + list + get for the operator-facing wizard launch
 * surface. The wizard creates a simulation then immediately runs it; the
 * BI Command Center Monte Carlo tab polls list endpoint for status updates.
 */

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === Types matching backend analytics_router.py ===

export type DistributionType = 'uniform' | 'normal' | 'triangular' | 'lognormal' | 'beta' | 'fixed';

export interface DistributionDef {
  type: DistributionType;
  params: Record<string, number>;
}

export interface CreateSimulationRequest {
  name: string;
  description?: string;
  input_distributions: Record<string, DistributionDef>;
  target_expression: string;
  num_trials: number;
  model_id?: string;
  deployment_id?: string;
  random_seed?: number;
}

export type SimulationStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface SimulationOut {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  status: SimulationStatus;
  num_trials: number;
  target_expression: string;
  results: Record<string, unknown> | null;
  created_at: string | null;
  completed_at: string | null;
}

// === API Functions ===

export async function createSimulation(
  authFetch: FetchFn,
  request: CreateSimulationRequest
): Promise<SimulationOut> {
  const res = await authFetch('/api/admin/ai/analytics/simulations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Create simulation failed: ${res.status}${detail ? ' — ' + detail : ''}`);
  }
  return res.json();
}

export async function runSimulation(
  authFetch: FetchFn,
  simulationId: string
): Promise<SimulationOut> {
  const res = await authFetch(`/api/admin/ai/analytics/simulations/${simulationId}/run`, {
    method: 'POST',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Run simulation failed: ${res.status}${detail ? ' — ' + detail : ''}`);
  }
  return res.json();
}

export async function listSimulations(
  authFetch: FetchFn,
  filters?: { status?: SimulationStatus; limit?: number; offset?: number }
): Promise<SimulationOut[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/analytics/simulations${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list simulations: ${res.status}`);
  return res.json();
}

export async function getSimulation(
  authFetch: FetchFn,
  simulationId: string
): Promise<SimulationOut> {
  const res = await authFetch(`/api/admin/ai/analytics/simulations/${simulationId}`);
  if (!res.ok) throw new Error(`Failed to get simulation: ${res.status}`);
  return res.json();
}
